import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { siteInfo, phoneToTelHref } from '../data/siteInfo';

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
  </svg>
);

const TwitterIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M4 4l16 16M4 20L20 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <path d="M2 3h6.5L21 21h-6.5L2 3z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
    <rect x="2" y="9" width="4" height="12"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);

const HouseLogo = () => (
  <svg width="28" height="28" viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <path
      d="M8 22.5L24 9l16 13.5V38a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2V22.5z"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinejoin="round"
    />
    <path d="M18 40V26h12v14" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
  </svg>
);

const Footer = () => {
  const { contact, social, legal } = siteInfo;
  const telHref = phoneToTelHref(contact.phone);

  const socials = [
    { icon: <FacebookIcon />, href: social.facebook, label: 'Facebook' },
    { icon: <TwitterIcon />, href: social.twitter, label: 'X / Twitter' },
    { icon: <InstagramIcon />, href: social.instagram, label: 'Instagram' },
    { icon: <LinkedInIcon />, href: social.linkedin, label: 'LinkedIn' }
  ].filter((s) => s.href);

  return (
    <footer style={{ background: 'var(--navy-deep)', color: '#fff' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '80px 24px 48px' }}>
        <div className="footer-main-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '48px'
        }}>
          <div>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <span style={{ color: 'var(--gold)', display: 'flex' }}>
                <HouseLogo />
              </span>
              <span style={{ fontFamily: 'var(--font-heading)', fontSize: '1rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {siteInfo.brandName}
              </span>
            </Link>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: '24px' }}>
              {siteInfo.shortDescription}
            </p>
            {socials.length > 0 && (
              <div style={{ display: 'flex', gap: '12px' }}>
                {socials.map(({ icon, href, label }) => (
                  <motion.a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    whileHover={{ y: -3, color: 'var(--gold)' }}
                    style={{
                      width: '38px', height: '38px',
                      background: 'rgba(255,255,255,0.08)',
                      borderRadius: '8px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'rgba(255,255,255,0.7)',
                      transition: 'all 0.2s'
                    }}
                  >
                    {icon}
                  </motion.a>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 style={{ fontFamily: 'var(--font-heading)', fontSize: '1rem', fontWeight: 700, marginBottom: '20px', color: 'var(--gold)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Quick Links
            </h4>
            <ul style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { to: '/', label: 'Home' },
                { to: '/about', label: 'About Us' },
                { to: '/services', label: 'Our Services' },
                { to: '/contact', label: 'Contact Us' }
              ].map(link => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.target.style.color = 'var(--gold)'}
                    onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.65)'}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 style={{ fontFamily: 'var(--font-heading)', fontSize: '1rem', fontWeight: 700, marginBottom: '20px', color: 'var(--gold)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Opening Hours
            </h4>
            <ul style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {siteInfo.openingHours.map((row) => (
                <li key={row.day} style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem', display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                  <span>{row.day}</span>
                  <span style={{ color: 'rgba(255,255,255,0.85)' }}>{row.hours}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 style={{ fontFamily: 'var(--font-heading)', fontSize: '1rem', fontWeight: 700, marginBottom: '20px', color: 'var(--gold)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Get in Touch
            </h4>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem', lineHeight: 1.8 }}>
              {contact.fullAddress && (
                <>
                  {contact.fullAddress}
                  <br />
                </>
              )}
              {contact.phone && (
                <>
                  {telHref ? (
                    <a href={telHref} style={{ color: 'rgba(255,255,255,0.65)' }}>
                      {contact.phone}
                    </a>
                  ) : (
                    contact.phone
                  )}
                  <br />
                </>
              )}
              <a href={`mailto:${contact.email}`} style={{ color: 'rgba(255,255,255,0.65)' }}>
                {contact.email}
              </a>
            </p>
            <Link to="/contact" className="btn-primary" style={{ marginTop: '20px', display: 'inline-flex' }}>
              Contact Us
            </Link>
          </div>
        </div>
      </div>

      <div style={{
        borderTop: '1px solid rgba(255,255,255,0.1)',
        padding: '20px 24px'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
            © {new Date().getFullYear()} {siteInfo.companyName}. Registered in England &amp; Wales.
            {legal.companyNumber ? <> Company No. {legal.companyNumber}.</> : null}
            {legal.vatNumber ? <> VAT {legal.vatNumber}.</> : null}
            {legal.tpoSchemeName ? <> Member of {legal.tpoSchemeName}{legal.tpoMembership ? ` (${legal.tpoMembership})` : ''}.</> : null}
            {' '}Based in Preston.
          </p>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            {[
              { to: '/privacy-policy', label: 'Privacy Policy' },
              { to: '/terms', label: 'Terms' },
              { to: '/cookie-policy', label: 'Cookie Policy' }
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem', transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = 'var(--gold)'}
                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.45)'}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          footer .footer-main-grid {
            grid-template-columns: 1fr 1fr !important;
            gap: 36px !important;
          }
        }
        @media (max-width: 560px) {
          footer .footer-main-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </footer>
  );
};

export default Footer;
